import { useQuery } from '@tanstack/react-query';
import { ArrowDown, ArrowUp, Link2, Plus, X } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';
import { toast } from 'sonner';

import { api, unwrap } from '@/api/client';
import { useCan } from '@/auth/queries';
import { Campo } from '@/components/Campo';
import { Cargando, Vacio } from '@/components/Estados';
import type { components } from '@/api/schema';

import { useManual, useManuales } from '../manuales/queries';
import { useResumenes } from '../resumenes/queries';
import { rutaClase, useClaseMutacion, type Clase } from './queries';

function mover<T>(lista: T[], i: number, d: -1 | 1): T[] {
  const n = [...lista];
  const j = i + d;
  if (j < 0 || j >= n.length) return n;
  [n[i], n[j]] = [n[j] as T, n[i] as T];
  return n;
}

function Guardar({
  sucio,
  ocupado,
  onGuardar,
}: {
  sucio: boolean;
  ocupado: boolean;
  onGuardar: () => void;
}) {
  return (
    <button
      type="button"
      className="btn-cs btn-primario"
      disabled={!sucio || ocupado}
      onClick={onGuardar}
    >
      Guardar cambios
    </button>
  );
}

/** Ultra Resumen: páginas de un manual (por omisión «Ultra resúmenes») elegidas en orden. */
export function PestanaUltra({ c }: { c: Clase }) {
  const editable = useCan('contenido', 'editar');
  const manuales = useManuales();
  const basicos = manuales.data?.filter((m) => m.edition === 'basic') ?? [];
  const porDefecto =
    basicos.find((m) => m.legacyId === 2) ??
    basicos.find((m) => /ultra/i.test(m.name)) ??
    basicos[0];
  const [manualId, setManualId] = useState<number | null>(null);
  const elegido = manualId ?? porDefecto?.id ?? null;
  const manual = useManual(elegido);
  const [sel, setSel] = useState(
    c.ultraPages.map((p) => ({ pageId: p.pageId, name: p.name, fileUrl: p.fileUrl })),
  );
  const [filtro, setFiltro] = useState('');
  const sucio =
    JSON.stringify(sel.map((s) => s.pageId)) !== JSON.stringify(c.ultraPages.map((p) => p.pageId));
  const guardar = useClaseMutacion(
    c.id,
    () =>
      unwrap<Clase>(
        api.PUT('/api/v1/admin/classes/{id}/ultra-pages', {
          ...rutaClase(c.id),
          body: { ids: sel.map((s) => s.pageId) },
        }),
      ),
    'Ultra Resumen guardado.',
  );
  const elegidas = new Set(sel.map((s) => s.pageId));
  const disponibles =
    manual.data?.pages.filter(
      (p) => !elegidas.has(p.id) && p.name.toLowerCase().includes(filtro.trim().toLowerCase()),
    ) ?? [];
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <section className="panel-vidrio space-y-3">
        <h3 className="font-extrabold">Páginas disponibles</h3>
        <div className="grid gap-2 sm:grid-cols-2">
          <Campo etiqueta="Manual">
            <select
              className="input-vidrio"
              value={elegido ?? ''}
              onChange={(e) => setManualId(Number(e.target.value))}
            >
              {basicos.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
            </select>
          </Campo>
          <Campo etiqueta="Buscar">
            <input
              className="input-vidrio"
              value={filtro}
              onChange={(e) => setFiltro(e.target.value)}
            />
          </Campo>
        </div>
        {manual.isPending && elegido !== null && <Cargando />}
        <ul className="max-h-[28rem] space-y-1 overflow-y-auto">
          {disponibles.slice(0, 300).map((p) => (
            <li key={p.id} className="flex items-center gap-2 text-sm">
              {p.fileUrl && (
                <img
                  src={p.fileUrl}
                  alt=""
                  loading="lazy"
                  className="h-9 w-12 rounded object-cover"
                />
              )}
              <span className="flex-1 truncate">{p.name}</span>
              {editable && (
                <button
                  type="button"
                  className="btn-cs btn-vidrio px-2 py-1"
                  aria-label={`Agregar ${p.name}`}
                  onClick={() =>
                    setSel([...sel, { pageId: p.id, name: p.name, fileUrl: p.fileUrl }])
                  }
                >
                  <Plus size={14} aria-hidden />
                </button>
              )}
            </li>
          ))}
        </ul>
      </section>
      <section className="panel-vidrio space-y-3">
        <div className="flex items-center justify-between gap-2">
          <h3 className="font-extrabold">En la clase ({sel.length})</h3>
          {editable && (
            <Guardar
              sucio={sucio}
              ocupado={guardar.isPending}
              onGuardar={() => guardar.mutate(undefined)}
            />
          )}
        </div>
        {sel.length === 0 && <Vacio titulo="Sin páginas" />}
        <ol className="space-y-1">
          {sel.map((s, i) => (
            <li key={s.pageId} className="flex items-center gap-2 text-sm">
              <span className="tabular w-6 text-texto-3">{i + 1}</span>
              {s.fileUrl && (
                <img
                  src={s.fileUrl}
                  alt=""
                  loading="lazy"
                  className="h-9 w-12 rounded object-cover"
                />
              )}
              <span className="flex-1 truncate">{s.name}</span>
              {editable && (
                <>
                  <button
                    type="button"
                    aria-label="Subir"
                    className="text-texto-3"
                    onClick={() => setSel(mover(sel, i, -1))}
                  >
                    <ArrowUp size={14} aria-hidden />
                  </button>
                  <button
                    type="button"
                    aria-label="Bajar"
                    className="text-texto-3"
                    onClick={() => setSel(mover(sel, i, 1))}
                  >
                    <ArrowDown size={14} aria-hidden />
                  </button>
                  <button
                    type="button"
                    aria-label={`Quitar ${s.name}`}
                    className="text-texto-3 hover:text-rojo-texto"
                    onClick={() => setSel(sel.filter((x) => x.pageId !== s.pageId))}
                  >
                    <X size={14} aria-hidden />
                  </button>
                </>
              )}
            </li>
          ))}
        </ol>
      </section>
    </div>
  );
}

/** Resúmenes GPC ligados a la clase. */
export function PestanaResumenes({ c }: { c: Clase }) {
  const editable = useCan('contenido', 'editar');
  const [sel, setSel] = useState(c.gpcSummaries);
  const [texto, setTexto] = useState('');
  const buscar = useResumenes({ q: texto.trim() || undefined, pageSize: 20 });
  const sucio =
    JSON.stringify(sel.map((s) => s.id).sort()) !==
    JSON.stringify(c.gpcSummaries.map((s) => s.id).sort());
  const guardar = useClaseMutacion(
    c.id,
    () =>
      unwrap<Clase>(
        api.PUT('/api/v1/admin/classes/{id}/gpc-summaries', {
          ...rutaClase(c.id),
          body: { ids: sel.map((s) => s.id) },
        }),
      ),
    'Resúmenes guardados.',
  );
  const ids = new Set(sel.map((s) => s.id));
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <section className="panel-vidrio space-y-3">
        <div className="flex items-center justify-between gap-2">
          <h3 className="font-extrabold">Ligados a la clase ({sel.length})</h3>
          {editable && (
            <Guardar
              sucio={sucio}
              ocupado={guardar.isPending}
              onGuardar={() => guardar.mutate(undefined)}
            />
          )}
        </div>
        {sel.length === 0 && <Vacio titulo="Sin resúmenes" />}
        <ul className="space-y-1">
          {sel.map((s) => (
            <li key={s.id} className="flex items-center gap-2 text-sm">
              <Link
                to={`/contenido/resumenes-gpc/${s.id}`}
                className="flex-1 truncate font-semibold hover:underline"
              >
                {s.name}
              </Link>
              {s.isUltra && <span className="insignia">Ultra</span>}
              {!s.isPublished && <span className="insignia">Oculto</span>}
              {editable && (
                <button
                  type="button"
                  aria-label={`Desligar ${s.name}`}
                  className="text-texto-3 hover:text-rojo-texto"
                  onClick={() => setSel(sel.filter((x) => x.id !== s.id))}
                >
                  <X size={14} aria-hidden />
                </button>
              )}
            </li>
          ))}
        </ul>
        {editable && (
          <Link
            to={`/contenido/resumenes-gpc?nuevo=1&classId=${c.id}`}
            className="btn-cs btn-vidrio"
          >
            <Plus size={16} aria-hidden /> Crear resumen para esta clase
          </Link>
        )}
      </section>
      {editable && (
        <section className="panel-vidrio space-y-3">
          <h3 className="font-extrabold">Ligar un resumen existente</h3>
          <input
            className="input-vidrio"
            aria-label="Buscar resumen"
            placeholder="Buscar por nombre o ID"
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
          />
          <ul className="max-h-96 space-y-1 overflow-y-auto">
            {buscar.data?.data
              .filter((r) => !ids.has(r.id))
              .map((r) => (
                <li key={r.id} className="flex items-center gap-2 text-sm">
                  <span className="flex-1 truncate">
                    {r.name}
                    {r.className && (
                      <span className="block text-xs text-texto-3">Hoy en: {r.className}</span>
                    )}
                  </span>
                  <button
                    type="button"
                    className="btn-cs btn-vidrio px-2 py-1"
                    aria-label={`Ligar ${r.name}`}
                    onClick={() =>
                      setSel([
                        ...sel,
                        { id: r.id, name: r.name, isPublished: r.isPublished, isUltra: r.isUltra },
                      ])
                    }
                  >
                    <Link2 size={14} aria-hidden />
                  </button>
                </li>
              ))}
          </ul>
        </section>
      )}
    </div>
  );
}

type OpcionPresentacion = components['schemas']['PresentationOption'];

/** Presentaciones de la clase y su asignación a las licencias de la clase. */
export function PestanaPresentacion({ c }: { c: Clase }) {
  const editable = useCan('contenido', 'editar');
  const [sel, setSel] = useState(
    c.presentations.map((p) => ({
      id: p.id,
      name: p.name,
      slidesCount: p.slidesCount,
      coverUrl: p.coverUrl,
    })),
  );
  const [texto, setTexto] = useState('');
  const opciones = useQuery({
    queryKey: ['contenido', 'presentaciones', 'opciones', texto.trim()],
    queryFn: () =>
      unwrap<OpcionPresentacion[]>(
        api.GET('/api/v1/admin/presentations/options', {
          params: { query: { q: texto.trim() || undefined } },
        }),
      ),
    enabled: editable,
  });
  const sucio =
    JSON.stringify(sel.map((s) => s.id)) !== JSON.stringify(c.presentations.map((p) => p.id));
  const guardar = useClaseMutacion(
    c.id,
    () =>
      unwrap<Clase>(
        api.PUT('/api/v1/admin/classes/{id}/presentations', {
          ...rutaClase(c.id),
          body: { ids: sel.map((s) => s.id) },
        }),
      ),
    'Presentación guardada.',
  );
  const asignar = useClaseMutacion(c.id, async () => {
    const r = await unwrap<{ changed: number }>(
      api.POST('/api/v1/admin/classes/{id}/presentations/assign-memberships', rutaClase(c.id)),
    );
    toast.success(
      r.changed === 0
        ? 'Ya estaban en las licencias de la clase.'
        : `${r.changed} asignación(es) agregada(s).`,
    );
    return unwrap<Clase>(api.GET('/api/v1/admin/classes/{id}', rutaClase(c.id)));
  });
  const ids = new Set(sel.map((s) => s.id));
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <section className="panel-vidrio space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="font-extrabold">En la clase</h3>
          {editable && (
            <Guardar
              sucio={sucio}
              ocupado={guardar.isPending}
              onGuardar={() => guardar.mutate(undefined)}
            />
          )}
        </div>
        {sel.length === 0 && <Vacio titulo="Sin presentación" />}
        <ul className="space-y-2">
          {sel.map((p) => (
            <li key={p.id} className="flex items-center gap-3 text-sm">
              {p.coverUrl && (
                <img src={p.coverUrl} alt="" className="h-12 w-20 rounded object-cover" />
              )}
              <Link
                to={`/contenido/presentaciones/${p.id}`}
                className="flex-1 font-semibold hover:underline"
              >
                {p.name}
                <span className="block text-xs font-normal text-texto-3">
                  {p.slidesCount} diapositivas
                </span>
              </Link>
              {editable && (
                <button
                  type="button"
                  aria-label={`Quitar ${p.name}`}
                  className="text-texto-3 hover:text-rojo-texto"
                  onClick={() => setSel(sel.filter((x) => x.id !== p.id))}
                >
                  <X size={14} aria-hidden />
                </button>
              )}
            </li>
          ))}
        </ul>
        {editable && c.presentations.length > 0 && (
          <div className="border-t border-[var(--separador)] pt-3">
            <p className="mb-2 text-xs text-texto-3">
              La presentación se ve dentro de la clase, pero también es un elemento de la
              biblioteca: asígnala a las {c.membershipIds.length} licencia(s) de la clase para que
              la vean esos alumnos.
            </p>
            <button
              type="button"
              className="btn-cs btn-vidrio"
              disabled={asignar.isPending || c.membershipIds.length === 0}
              onClick={() => asignar.mutate(undefined)}
            >
              <Link2 size={16} aria-hidden /> Asignar a las licencias de la clase
            </button>
          </div>
        )}
      </section>
      {editable && (
        <section className="panel-vidrio space-y-3">
          <h3 className="font-extrabold">Elegir presentación</h3>
          <input
            className="input-vidrio"
            aria-label="Buscar presentación"
            placeholder="Buscar por nombre o ID"
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
          />
          <ul className="max-h-96 space-y-2 overflow-y-auto">
            {opciones.data
              ?.filter((o) => !ids.has(o.id))
              .map((o) => (
                <li key={o.id} className="flex items-center gap-3 text-sm">
                  {o.coverUrl ? (
                    <img
                      src={o.coverUrl}
                      alt=""
                      loading="lazy"
                      className="h-10 w-16 rounded object-cover"
                    />
                  ) : (
                    <span className="h-10 w-16 rounded bg-black/5" />
                  )}
                  <span className="flex-1">
                    {o.name}
                    <span className="block text-xs text-texto-3">
                      #{o.id} · {o.slidesCount} diapositivas
                    </span>
                  </span>
                  <button
                    type="button"
                    className="btn-cs btn-vidrio px-2 py-1"
                    aria-label={`Elegir ${o.name}`}
                    onClick={() => setSel([...sel, o])}
                  >
                    <Plus size={14} aria-hidden />
                  </button>
                </li>
              ))}
          </ul>
        </section>
      )}
    </div>
  );
}
